import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControlLabel,
    Radio,
    RadioGroup,
    Tooltip,
    colors,
} from '@material-ui/core';
import { Lock as LockIcon } from '@material-ui/icons';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { selectFightWinner } from 'actions';
import BracketView, { isFullyPaired } from './BracketView';
import { bracketSlotAnnotations } from './slotAnnotations';

const useStyles = makeStyles(theme => ({
    lockBanner: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    lockIcon: {
        color: colors.grey[600],
    },
    classPoints: {
        marginLeft: theme.spacing(1),
        color: theme.palette.text.secondary,
        fontVariantNumeric: 'tabular-nums',
    },
}));

/*
 * HOW AN MSAC BOUT WAS WON, and the penalty points it gives — international
 * rules 2025, «Всестильовий бій» §1.5: [winner, loser].
 */
const VICTORY_CLASSES = [
    { value: 'clean', points: '0 / 5' },
    { value: 'on_points', points: '1 / 4' },
    { value: 'slight_advantage', points: '2 / 3' },
];

/**
 * The conductable bracket: BracketView (pure rendering) + interaction.
 *
 * Owns every interaction DECISION the renderer must not know about:
 *  - which stages are FIGHTS is the backend's call: a kata draw mixes pair
 *    rounds with score tables, so "fully paired" is not the test. Only when
 *    the backend sent no execution info (legacy categories) does the
 *    historical fully-paired rule apply;
 *  - `readOnly` (permissions / workflow status FINISHED) disables clicking;
 *  - the EXECUTION ENGINE decides how each round is judged: winner clicks
 *    are offered ONLY on flags-judged rounds — score-judged rounds are
 *    conducted through the score panel and their slots show the entered
 *    score instead (backend returns the mode; this component just renders);
 *  - a click on an occupied slot declares that athlete the fight's winner —
 *    the BACKEND advances the winner, feeds the third-place fight and clears
 *    invalidated rounds; the returned draw replaces the category rows in the
 *    store, so the bracket updates live without a reload.
 *
 * Future interactions (walkover, disqualification, injury, technical
 * victory, referee decisions, live tatami mode) belong HERE — the renderer
 * stays untouched.
 */
const InteractiveBracketView = ({ results, getParticipantName, getCompetitorInfo, getAttendanceStatus, getNationalTeamMember, competition, category, readOnly, execution, pointsStageLocked, roundOrder, layout, protocols, onProtocolOpen, nextFight, currentFight }) => {
    const classes = useStyles();
    const [t] = useTranslation();
    const dispatch = useDispatch();
    // Rounds the strategy judges by flags — the only clickable ones. When
    // no execution info exists (legacy categories) every round stays
    // clickable, exactly the historical behaviour.
    // FUKUGO: which discipline each round is fought in, as the backend derived
    // it. Empty for every other category, so the label appears nowhere else.
    const roundStages = execution && Array.isArray(execution.rounds)
        ? execution.rounds.reduce((acc, r) => (
            r.fukugoStage ? Object.assign(acc, { [r.round]: r.fukugoStage }) : acc
        ), {})
        : null;

    const flagsRounds = execution && Array.isArray(execution.rounds)
        ? execution.rounds.filter(r => r.mode === 'flags').map(r => r.round)
        : null;

    // A kata draw is pair rounds FEEDING a score table, so it is never fully
    // paired — requiring that left its fight stage unconductable and the
    // whole competition stuck before the first score table. The per-round
    // gate below is what keeps list rounds unclickable.
    const canConduct = !readOnly && !pointsStageLocked && (flagsRounds !== null || isFullyPaired(results));

    // What each slot carries beside the name — shared with the draw tab.
    const slotAnnotations = bracketSlotAnnotations(results, t);

    // The backend says which bouts need it (Б-1…Б-4, Б-7) — never FSKA.
    const victoryClassRequired = Boolean(execution && execution.victoryClassRequired);

    /**
     * The sheet the fight this row belongs to ran on, if any.
     *
     * Keyed by round and pair, which is how a protocol addresses a fight. Two
     * things follow: the button that opens it, and whether the slot may be
     * clicked.
     *
     * A CONFIRMED sheet carries a timeline, scores and a signature, and one
     * stray click here would overwrite all of that with a bare winner — so it
     * stays unclickable, and a deliberate change goes through the correction
     * workflow.
     *
     * …WHILE THE FIGHT IT SIGNED IS STILL DECIDED. A draw deleted and
     * generated again leaves the protocol behind, addressed by round and pair
     * — numbers the new bracket reuses — and it then locked a slot in a
     * bracket where nobody had won anything, with no way out from anywhere
     * (1 Oct 2026). See `isDecidedProtocol` in BracketView.
     *
     * AN OPEN SHEET IS THE OPPOSITE CASE. Until 1 Oct 2026 any protocol at all
     * locked the fight, so a mat that opened a sheet and then lost its
     * computer left the bout impossible to finish from anywhere: the mat could
     * not reach it and the bracket refused. The slot is clickable now, behind
     * the confirmation below, because somebody has to be able to end the
     * fight.
     */
    const protocolsByFight = (protocols || []).reduce(
        (acc, protocol) => Object.assign(acc, { [`${protocol.round}|${protocol.pair}`]: protocol }),
        {},
    );

    const protocolFor = result => (
        result ? (protocolsByFight[`${result.round}|${result.pair}`] || null) : null
    );
    // A REAL SHEET, as opposed to the record of a fight decided by a click
    // with no sheet at all (`id` null, 25 Oct 2026): that one gets «i» but
    // never locks a slot or asks about discarding a sheet.
    const sheetFor = result => {
        const protocol = protocolFor(result);

        return protocol && protocol.id ? protocol : null;
    };

    /* Whether the fight this row belongs to still carries a winner — the same
       question BracketView asks to decide whether a signed sheet still
       protects anything. */
    const decidedFights = (results || []).reduce((acc, row) => {
        const key = `${row.round}|${row.pair}`;
        acc[key] = acc[key] || true === row.win;

        return acc;
    }, {});
    const fightDecided = result => (
        result ? Boolean(decidedFights[`${result.round}|${result.pair}`]) : true
    );

    /** A pick waiting for the operator to confirm discarding an open sheet. */
    const [pendingOverride, setPendingOverride] = useState(null);
    /** A pick waiting for how the bout was won (MSAC), and the choice so far. */
    const [pendingClass, setPendingClass] = useState(null);
    const [victoryClass, setVictoryClass] = useState('');

    const record = (result, chosenClass = null) =>
        dispatch(selectFightWinner(result.id, result.participant.id, competition, category, chosenClass));

    const decide = result => {
        if (victoryClassRequired) {
            setVictoryClass('');
            setPendingClass(result);

            return;
        }
        record(result);
    };

    const handleSlotClick = result => {
        if (!canConduct || !result || !result.participant) {
            return;
        }
        // A signed result is never rewritten from a review screen. The slot is
        // already rendered unclickable; this stays because the rendering guard
        // lives in another component.
        const protocol = sheetFor(result);
        if (protocol && 'CONFIRMED' === protocol.state && fightDecided(result)) {
            return;
        }
        if (flagsRounds && !flagsRounds.includes(result.round)) {
            return;
        }

        /*
         * AN OPEN SHEET IS DISCARDED, SO IT IS ASKED ABOUT.
         *
         * The mat may have recorded points, a clock, a kata — none of it
         * survives a winner decided here. That is the right trade when the
         * mat is unreachable and wrong as an accident, which is exactly what
         * a confirmation is for. A fight with no sheet at all is decided on
         * the click, as it always was.
         */
        if (protocol) {
            setPendingOverride(result);

            return;
        }

        decide(result);
    };

    const overrideName = pendingOverride && getParticipantName
        ? getParticipantName(pendingOverride.participant)
        : null;

    const className = pendingClass && getParticipantName
        ? getParticipantName(pendingClass.participant)
        : null;

    return (
        <>
            {/* HOW WAS IT WON (MSAC, 21 Oct 2026) — required: the winner
                takes 0, 1 or 2 penalty points, the other the rest of 5. */}
            <Dialog
                open={Boolean(pendingClass)}
                onClose={() => setPendingClass(null)}
                maxWidth="xs"
                fullWidth
                data-testid='bracket-victory-class-dialog'
            >
                <DialogTitle>
                    {t('workspace.matches.victoryClass.title', { winner: className || '', defaultValue: 'How did {{winner}} win?' })}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('workspace.matches.victoryClass.hint', 'Penalty points, winner / loser — they order the athletes who share a place.')}
                    </DialogContentText>
                    <RadioGroup value={victoryClass} onChange={event => setVictoryClass(event.target.value)}>
                        {VICTORY_CLASSES.map(option => (
                            <FormControlLabel
                                key={option.value}
                                value={option.value}
                                control={<Radio color="primary" inputProps={{ 'data-testid': `bracket-victory-class-${option.value}` }} />}
                                label={(
                                    <span>
                                        {t(`workspace.matches.victoryClass.${option.value}`)}
                                        <span className={classes.classPoints}>{option.points}</span>
                                    </span>
                                )}
                            />
                        ))}
                    </RadioGroup>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPendingClass(null)}>
                        {t('workspace.matches.overrideOpenSheet.cancel', 'Cancel')}
                    </Button>
                    <Button
                        color="primary"
                        variant="contained"
                        disabled={!victoryClass}
                        data-testid='bracket-victory-class-confirm'
                        onClick={() => {
                            const result = pendingClass;
                            setPendingClass(null);
                            record(result, victoryClass);
                        }}
                    >
                        {t('workspace.matches.overrideOpenSheet.confirm', 'Record the winner')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={Boolean(pendingOverride)}
                onClose={() => setPendingOverride(null)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>
                    {t('workspace.matches.overrideOpenSheet.title', 'Finish this fight here?')}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText data-testid='bracket-override-text'>
                        {t(
                            'workspace.matches.overrideOpenSheet.body',
                            'The mat has a sheet open for this fight. Recording {{winner}} as the winner here discards whatever it holds — scores, penalties, the clock. Use this when the mat cannot finish the fight itself.',
                            { winner: overrideName || '' },
                        )}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPendingOverride(null)}>
                        {t('workspace.matches.overrideOpenSheet.cancel', 'Cancel')}
                    </Button>
                    <Button
                        color="primary"
                        variant="contained"
                        data-testid='bracket-override-confirm'
                        onClick={() => {
                            const result = pendingOverride;
                            setPendingOverride(null);
                            decide(result);
                        }}
                    >
                        {t('workspace.matches.overrideOpenSheet.confirm', 'Record the winner')}
                    </Button>
                </DialogActions>
            </Dialog>

            {pointsStageLocked && (
                <div className={classes.lockBanner}>
                    <Tooltip title={t(
                        'workspace.matches.bracketLockedByPointsStage',
                        'The bracket is locked because the Points stage has already been generated. Reset the Points stage before modifying Flag results.'
                    )}>
                        <LockIcon
                            fontSize='small'
                            className={classes.lockIcon}
                            data-testid='matches-bracket-lock-icon'
                        />
                    </Tooltip>
                </div>
            )}
            <BracketView
                results={results}
                getParticipantName={getParticipantName}
                getCompetitorInfo={getCompetitorInfo}
                getAttendanceStatus={getAttendanceStatus}
                getNationalTeamMember={getNationalTeamMember}
                clickableSlots={canConduct}
                onSlotClick={handleSlotClick}
                protocolFor={protocolFor}
                onProtocolOpen={onProtocolOpen}
                nextFight={nextFight}
                currentFight={currentFight}
                clickableRounds={flagsRounds}
                slotAnnotations={slotAnnotations}
                roundOrder={roundOrder}
                layout={layout || (execution && execution.layout) || undefined}
                roundStages={roundStages}
            />
        </>
    );
};

InteractiveBracketView.propTypes = {
    results: PropTypes.array.isRequired,
    getParticipantName: PropTypes.func,
    getCompetitorInfo: PropTypes.func,
    getAttendanceStatus: PropTypes.func,
    getNationalTeamMember: PropTypes.func,
    competition: PropTypes.string.isRequired,
    category: PropTypes.string.isRequired,
    readOnly: PropTypes.bool,
    execution: PropTypes.object,
    pointsStageLocked: PropTypes.bool,
    /** Every fight protocol of this category, as the API published them. */
    protocols: PropTypes.array,
    /** (protocol) => void — show it. Without this no button is rendered. */
    onProtocolOpen: PropTypes.func,
    nextFight: PropTypes.object,
    currentFight: PropTypes.object,
    // Explicit play order — supplied for a repechage ladder, whose rungs all
    // hold two rows and so cannot be ordered by size. See BracketView.
    roundOrder: PropTypes.array,
    /**
     * 'linear' or 'mirrored'. Normally taken from the backend-published
     * execution state; the ladders pass 'linear' explicitly, because a
     * consolation ladder is a chain and has no halves to face each other.
     */
    layout: PropTypes.string,
};

InteractiveBracketView.defaultProps = {
    getParticipantName: null,
    getCompetitorInfo: null,
    getAttendanceStatus: null,
    getNationalTeamMember: null,
    readOnly: false,
    execution: null,
    pointsStageLocked: false,
};

export default InteractiveBracketView;
